import React, { useState, useRef, Fragment, lazy, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ArrowRightIcon, TagIcon, ChevronDownIcon, ExternalLinkIcon, NewspaperIcon, TrophyIcon, InstagramIcon, FacebookIcon, PlayCircleIcon, XIcon, Share2Icon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BLOG_POSTS, BlogPost } from '../data/blog-posts';
// ============================================================
//  Blog posts data is now in data/blog-posts.ts
//  Edit that file to add or modify blog posts.
// ============================================================
const ALL_CATEGORIES = ['All', 'Political', 'Immigration', 'Business', 'Note Worthy', 'Events', 'Arts & Entertainment', 'Sports'];
const CATEGORY_COLORS: Record<string, string> = {
  Political: 'bg-haa-blue text-white',
  Immigration: 'bg-haa-skyblue text-white',
  Business: 'bg-emerald-600 text-white',
  'Note Worthy': 'bg-haa-midblue text-white',
  'Arts & Entertainment': 'bg-haa-navy text-white',
  Events: 'bg-blue-400 text-white',
  Sports: 'bg-orange-500 text-white'
};
const GRADIENT_FALLBACKS: Record<string, string> = {
  Political: 'from-haa-blue via-blue-700 to-haa-navy',
  Immigration: 'from-haa-skyblue to-haa-midblue',
  Business: 'from-emerald-500 to-emerald-800',
  'Note Worthy': 'from-haa-midblue to-haa-navy',
  'Arts & Entertainment': 'from-haa-navy to-blue-950',
  Events: 'from-blue-400 to-haa-midblue',
  Sports: 'from-orange-500 to-red-600'
};
function getCategoryColor(category: string) {
  return CATEGORY_COLORS[category] ?? 'bg-haa-blue text-white';
}
function getGradient(category: string) {
  return GRADIENT_FALLBACKS[category] ?? 'from-haa-blue to-haa-navy';
}
// Story Modal Component
function StoryModal({
  isOpen,
  onClose,
  post,
  translatedTitle,
  translatedDate,
  translatedFullStory,
  isEs,
  t
}: {
  isOpen: boolean;
  onClose: () => void;
  post: BlogPost;
  translatedTitle: string;
  translatedDate: string;
  translatedFullStory?: string;
  isEs: boolean;
  t: any;
}) {
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  return <AnimatePresence data-id="element-2871">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-2872">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2873">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white sticky top-0 z-10" data-id="element-2874">
              <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-2875">
                {post.category}
              </span>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-2876">
                <XIcon className="w-5 h-5" data-id="element-2877" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto" data-id="element-2878">
              {post.image && !(post.players && post.players.length > 0) && <div className="w-full h-80 sm:h-96 relative" data-id="element-2879">
                  <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover" style={{
              objectPosition: post.imagePosition || 'center top'
            }} data-id="element-2880" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-2881" />
                </div>}

              {/* Video Embeds — shown at top when no image */}
              {!post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="w-full" data-id="element-2882">
                    {post.videoEmbeds.map((video, vi) => <div key={vi} className="bg-black relative" data-id="element-2883">
                        {video.youtubeId ? <div className="relative w-full" style={{
                paddingBottom: '56.25%'
              }} data-id="element-2884">
                            <iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                  border: 0
                }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-2885" />
                            {/* Fallback thumbnail link */}
                            <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 -z-10 flex flex-col items-center justify-center bg-black" data-id="element-2886">
                              <img src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={video.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-2887" />
                              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2" data-id="element-2888">
                                <PlayCircleIcon className="w-14 h-14 text-white/90 drop-shadow-lg" data-id="element-2889" />
                                <span className="font-nunito text-white text-xs font-bold bg-red-600 px-3 py-1.5 rounded-full shadow-lg" data-id="element-2890">
                                  Watch on YouTube
                                </span>
                              </div>
                            </a>
                          </div> : null}
                      </div>)}
                  </div>}

              <div className="p-6 sm:p-8" data-id="element-2891">
                <div className="flex items-center gap-2 mb-4" data-id="element-2892">
                  <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-2893" />
                  <span className="font-nunito text-gray-500 text-sm font-semibold" data-id="element-2894">
                    {translatedDate}
                  </span>
                </div>

                <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-6" data-id="element-2895">
                  {translatedTitle}
                </h2>

                {translatedFullStory && <div className="prose prose-lg max-w-none font-nunito text-gray-600 leading-relaxed whitespace-pre-line mb-8" data-id="element-2896">
                    {translatedFullStory}
                  </div>}

                {/* Video Embeds — shown after story when post has image */}
                {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mb-8 space-y-4" data-id="element-2897">
                      {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black relative" data-id="element-2898">
                          {video.youtubeId ? <div className="relative w-full" style={{
                  paddingBottom: '56.25%'
                }} data-id="element-2899">
                              <iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                    border: 0
                  }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-2900" />
                              <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 -z-10 flex flex-col items-center justify-center bg-black" data-id="element-2901">
                                <img src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={video.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-2902" />
                                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2" data-id="element-2903">
                                  <PlayCircleIcon className="w-14 h-14 text-white/90 drop-shadow-lg" data-id="element-2904" />
                                  <span className="font-nunito text-white text-xs font-bold bg-red-600 px-3 py-1.5 rounded-full shadow-lg" data-id="element-2905">
                                    Watch on YouTube
                                  </span>
                                </div>
                              </a>
                            </div> : null}
                        </div>)}
                    </div>}

                {/* Story Images Gallery */}
                {post.storyImages && post.storyImages.length > 0 && <div className="mb-8 space-y-4" data-id="element-2906">
                    {post.storyImages.map((img, idx) => <div key={idx} className="rounded-xl overflow-hidden shadow-md" data-id="element-2907">
                        <img src={img} alt={`${translatedTitle} — ${idx + 1}`} className="w-full h-auto object-cover" data-id="element-2908" />
                      </div>)}
                  </div>}

                {/* Players */}
                {post.players && post.players.length > 0 && <div className="space-y-3 mb-8" data-id="element-2909">
                    <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-4" data-id="element-2910">
                      {isEs ? 'Jugadores Destacados' : 'Featured Players'}
                    </h3>
                    {post.players.map(player => {
                const isExpanded = expandedPlayer === player.name;
                const playerKeyMap: Record<string, string> = {
                  'Oscar Avilez': 'avilez',
                  'David Ruíz': 'ruiz',
                  'Joseph Rosales': 'rosales',
                  'Andy Nájar': 'najar',
                  'Bryan Acosta': 'acosta'
                };
                const playerKey = playerKeyMap[player.name] ?? '';
                const translatedBio = playerKey ? t(`news.player.${playerKey}.bio`) : player.bio;
                const translatedPosition = player.position === 'Midfielder' ? t('news.player.position.midfielder') : player.position === 'Defender' ? t('news.player.position.defender') : player.position;
                return <div key={player.name} className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50" data-id="element-2911">
                          <button onClick={() => setExpandedPlayer(isExpanded ? null : player.name)} className="w-full flex items-center gap-4 px-4 py-3 hover:bg-gray-100 transition-colors text-left" aria-expanded={isExpanded} data-id="element-2912">
                            {player.image ? <img src={player.image} alt={player.name} className="w-16 h-16 rounded-full object-cover object-top shadow-md flex-shrink-0 border-2 border-white" data-id="element-2913" /> : <div className={`w-16 h-16 rounded-full ${player.teamColor} flex items-center justify-center text-white font-nunito font-bold text-lg shadow-md flex-shrink-0`} data-id="element-2914">
                                {player.name.split(' ').map(n => n[0]).join('')}
                              </div>}
                            <div className="flex-1 min-w-0" data-id="element-2915">
                              <p className="font-nunito text-sm font-bold text-haa-navy leading-tight" data-id="element-2916">
                                {player.name}
                                <span className="text-gray-400 font-normal ml-1.5" data-id="element-2917">
                                  #{player.number}
                                </span>
                              </p>
                              <p className="font-nunito text-xs text-gray-500 mt-0.5" data-id="element-2918">
                                {translatedPosition} · {player.team}
                              </p>
                            </div>
                            <motion.span animate={{
                      rotate: isExpanded ? 180 : 0
                    }} transition={{
                      duration: 0.2
                    }} className="flex-shrink-0" data-id="element-2919">
                              <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-2920" />
                            </motion.span>
                          </button>
                          <AnimatePresence initial={false} data-id="element-2921">
                            {isExpanded && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.25,
                      ease: [0.16, 1, 0.3, 1]
                    }} className="overflow-hidden" data-id="element-2922">
                                <div className="px-4 pb-4 pt-1 border-t border-gray-200" data-id="element-2923">
                                  <p className="font-nunito text-sm text-gray-600 leading-relaxed" data-id="element-2924">
                                    {translatedBio}
                                  </p>
                                  <a href={player.profileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue transition-colors" data-id="element-2925">
                                    {t('news.player.viewProfile')}
                                    <ExternalLinkIcon className="w-3 h-3" data-id="element-2926" />
                                  </a>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
              })}
                  </div>}

                {/* Video Embeds — shown after story when there is an image */}
                {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mb-8" data-id="element-2927">
                      <div className="flex items-center gap-2 mb-4" data-id="element-2928">
                        <PlayCircleIcon className="w-5 h-5 text-amber-600" data-id="element-2929" />
                        <h3 className="font-bebas text-2xl text-haa-navy tracking-wider" data-id="element-2930">
                          {isEs ? 'Videos' : 'Videos'}
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2931">
                        {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black" data-id="element-2932">
                            {video.youtubeId ? <div className="relative w-full" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-2933">
                                <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-2934" />
                              </div> : video.facebookUrl ? <a href={video.facebookUrl} target="_blank" rel="noopener noreferrer" className="relative block w-full bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 transition-colors" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-2935">
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" data-id="element-2936">
                                  <FacebookIcon className="w-8 h-8 text-blue-400" data-id="element-2937" />
                                  <span className="font-nunito text-white text-sm font-semibold" data-id="element-2938">
                                    {video.label}
                                  </span>
                                  <span className="font-nunito text-white/50 text-xs" data-id="element-2939">
                                    {isEs ? 'Ver en Facebook' : 'Watch on Facebook'}
                                  </span>
                                </div>
                              </a> : null}
                            <div className="px-4 py-3 bg-gray-900" data-id="element-2940">
                              <p className="font-nunito text-white/80 text-xs font-semibold truncate" data-id="element-2941">
                                {video.label}
                              </p>
                            </div>
                          </div>)}
                      </div>
                    </div>}

                {/* Social Links */}
                {post.socialLinks && post.socialLinks.length > 0 && <div className="mb-8 pt-6 border-t border-gray-100" data-id="element-2942">
                    <h3 className="font-bebas text-xl text-haa-navy tracking-wider mb-4" data-id="element-2943">
                      {isEs ? 'Síguelos' : 'Follow Them'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-2944">
                      {post.socialLinks.map((social, si) => <a key={si} href={social.url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-nunito text-sm font-bold transition-all duration-200 ${social.platform === 'instagram' ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white hover:shadow-lg hover:shadow-pink-500/25 hover:scale-105' : 'bg-[#1877F2] text-white hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105'}`} aria-label={social.label} data-id="element-2945">
                          {social.platform === 'instagram' ? <InstagramIcon className="w-4 h-4" data-id="element-2946" /> : <FacebookIcon className="w-4 h-4" data-id="element-2947" />}
                          {social.label}
                        </a>)}
                    </div>
                  </div>}

                {/* Coverage Links */}
                {post.coverageLinks && post.coverageLinks.length > 0 && <div className="pt-6 border-t border-gray-100" data-id="element-2948">
                    <div className="flex items-center gap-2 mb-3" data-id="element-2949">
                      <NewspaperIcon className="w-4 h-4 text-gray-400" data-id="element-2950" />
                      <span className="font-nunito text-gray-500 text-sm uppercase tracking-wider font-bold" data-id="element-2951">
                        {t('news.alsoCoveredBy')}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-2952">
                      {post.coverageLinks.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 font-nunito text-sm font-bold text-haa-midblue hover:text-haa-blue transition-colors border border-gray-200" data-id="element-2953">
                          {link.label}
                          <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-2954" />
                        </a>)}
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// Featured Card
function FeaturedCard({
  post
}: {
  post: BlogPost;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postIdMap: Record<string, string> = {
    'post-1': '1',
    'post-7': '7',
    'post-2': '2',
    'post-5': '5',
    'post-6': '6',
    'post-8': '8',
    'post-9': '9',
    'post-10': '10',
    'post-11': '11',
    'post-12': '12',
    'post-13': '13',
    'post-14': '14',
    'post-15': '15',
    'post-16': '16',
    'post-17': '17',
    'post-18': '18',
    'post-19': '19',
    'post-20': '20',
    'post-21': '21',
    'post-22': '22',
    'post-23': '23',
    'post-24': '24',
    'post-25': '25',
    'post-26': '26',
    'post-27': '27',
    'post-28': '28',
    'post-29': '29',
    'post-30': '30',
    'post-31': '31',
    'post-32': '32',
    'post-33': '33',
    'post-34': '34',
    'post-35': '35',
    'post-36': '36',
    'post-37': '37'
  };
  const postNum = postIdMap[post.id] ?? '';
  const titleKey = postNum ? `news.post${postNum}.title` : '';
  const titleTranslation = titleKey ? t(titleKey) : '';
  const translatedTitle = titleKey && titleTranslation !== titleKey && titleTranslation !== '' ? titleTranslation : post.title;
  const excerptKey = postNum ? `news.post${postNum}.excerpt` : '';
  const excerptTranslation = excerptKey ? t(excerptKey) : '';
  const translatedExcerpt = excerptKey && excerptTranslation !== excerptKey ? excerptTranslation : post.excerpt;
  const dateKey = postNum ? `news.post${postNum}.date` : '';
  const dateTranslation = dateKey ? t(dateKey) : '';
  const translatedDate = dateKey && dateTranslation !== dateKey ? dateTranslation : post.date;
  const fullStoryKey = postNum ? `news.post${postNum}.fullStory` : '';
  const fullStoryTranslation = fullStoryKey ? t(fullStoryKey) : '';
  const translatedFullStory = fullStoryKey && fullStoryTranslation !== fullStoryKey ? fullStoryTranslation : post.fullStory;
  const hasStoryUrl = post.storyUrl && post.storyUrl !== '#';
  const hasFullStory = !!post.fullStory || !!post.players || !!post.videoEmbeds;
  const categoryKeyMap: Record<string, string> = {
    Political: 'news.catPolitical',
    Immigration: 'news.catImmigration',
    Business: 'news.catBusiness',
    'Note Worthy': 'news.catNoteWorthy',
    'Arts & Entertainment': 'news.catArtMedia',
    Events: 'news.catEvents',
    Sports: 'news.catSports'
  };
  const translatedCategory = categoryKeyMap[post.category] ? t(categoryKeyMap[post.category]) : post.category;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-xl lg:col-span-2 group" data-id="element-2955">
      <div className="relative h-64 sm:h-80 overflow-hidden" data-id="element-2956">
        {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition || 'center 25%'
      }} data-id="element-2957" /> : !post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && post.videoEmbeds[0].youtubeId ? <iframe src={`https://www.youtube.com/embed/${post.videoEmbeds[0].youtubeId}`} title={post.videoEmbeds[0].label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-2958" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-2959">
            <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 30% 70%, white 1px, transparent 1px)',
          backgroundSize: '25px 25px'
        }} data-id="element-2960" />
          </div>}
        {!(post.videoEmbeds && post.videoEmbeds.length > 0 && !post.image) && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-2961" />}
        <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2962">
          <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)} mb-3 inline-block`} data-id="element-2963">
            {translatedCategory}
          </span>
          {!(post.videoEmbeds && post.videoEmbeds.length > 0 && !post.image) && <p className="font-bebas text-white text-3xl sm:text-4xl leading-tight tracking-wider line-clamp-2" data-id="element-2964">
              {translatedTitle}
            </p>}
        </div>
      </div>

      <div className="p-6 sm:p-8" data-id="element-2965">
        <div className="flex items-center gap-3 mb-4" data-id="element-2966">
          <div className="w-8 h-1 bg-haa-blue rounded-full" data-id="element-2967" />
          <span className="font-nunito text-haa-blue text-xs font-bold uppercase tracking-widest" data-id="element-2968">
            {t('news.featuredStory')}
          </span>
        </div>

        <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-3" data-id="element-2969">
          {translatedTitle}
        </h2>

        <div className="mb-6" data-id="element-2970">
          <p className="font-nunito text-gray-600 leading-relaxed" data-id="element-2971">
            {translatedExcerpt}
          </p>

          {/* Story preview — show first 2 paragraphs of fullStory */}
          {translatedFullStory && <div className="relative mt-4" data-id="element-2972">
              <div className="font-nunito text-gray-500 text-sm leading-relaxed space-y-3" data-id="element-2973">
                {translatedFullStory.split('\n\n').slice(0, 2).map((para, i) => <p key={i} data-id="element-2974">{para}</p>)}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" data-id="element-2975" />
            </div>}

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5" data-id="element-2976">
            {hasStoryUrl ? <a href={post.storyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-haa-blue text-white font-nunito font-bold text-sm px-5 py-2.5 rounded-full hover:bg-haa-midblue transition-colors shadow-md hover:shadow-lg" data-id="element-2977">
                {t('news.readFullStory')}
                <ExternalLinkIcon className="w-4 h-4" data-id="element-2978" />
              </a> : hasFullStory ? <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 bg-haa-blue text-white font-nunito font-bold text-sm px-5 py-2.5 rounded-full hover:bg-haa-midblue transition-colors shadow-md hover:shadow-lg" data-id="element-2979">
                {t('news.readFullStory')}
                <ArrowRightIcon className="w-4 h-4" data-id="element-2980" />
              </button> : null}

            {post.coverageLinks && post.coverageLinks.length > 0 && <>
                <span className="text-gray-200 select-none hidden sm:inline" data-id="element-2981">
                  |
                </span>
                <div className="flex items-center gap-2 flex-wrap" data-id="element-2982">
                  <NewspaperIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-2983" />
                  <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-2984">
                    {t('news.alsoCoveredBy')}
                  </span>
                  {post.coverageLinks.map((link, i) => <Fragment key={link.url} data-id="element-2985">
                      {i > 0 && <span className="text-gray-200 select-none" data-id="element-2986">·</span>}
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-2987">
                        {link.label}
                        <ExternalLinkIcon className="w-3 h-3" data-id="element-2988" />
                      </a>
                    </Fragment>)}
                </div>
              </>}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-2989">
          <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm" data-id="element-2990">
            <span className="flex items-center gap-1.5" data-id="element-2991">
              <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-2992" />
              <span className="font-nunito" data-id="element-2993">{translatedDate}</span>
            </span>
          </div>
          <a href={`#/news/${post.id}?lang=${language}`} className="inline-flex items-center gap-1.5 text-haa-blue hover:text-haa-midblue font-nunito font-bold text-xs transition-colors" title={isEs ? 'Abrir historia' : 'Open story'} data-id="element-2994">
            <Share2Icon className="w-3.5 h-3.5" data-id="element-2995" />
            {isEs ? 'Compartir' : 'Share'}
          </a>
        </div>
      </div>

      <StoryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} post={post} translatedTitle={translatedTitle} translatedDate={translatedDate} translatedFullStory={translatedFullStory} isEs={isEs} t={t} data-id="element-2996" />
    </motion.article>;
}
// Regular Card
function PostCard({
  post,
  index
}: {
  post: BlogPost;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postIdMap: Record<string, string> = {
    'post-1': '1',
    'post-7': '7',
    'post-2': '2',
    'post-5': '5',
    'post-6': '6',
    'post-8': '8',
    'post-9': '9',
    'post-10': '10',
    'post-11': '11',
    'post-12': '12',
    'post-13': '13',
    'post-14': '14',
    'post-15': '15',
    'post-16': '16',
    'post-17': '17',
    'post-18': '18',
    'post-19': '19',
    'post-20': '20',
    'post-21': '21',
    'post-22': '22',
    'post-23': '23',
    'post-24': '24',
    'post-25': '25',
    'post-26': '26',
    'post-27': '27',
    'post-28': '28',
    'post-29': '29',
    'post-30': '30',
    'post-31': '31',
    'post-32': '32',
    'post-33': '33',
    'post-34': '34',
    'post-35': '35',
    'post-36': '36',
    'post-37': '37'
  };
  const postNum = postIdMap[post.id] ?? '';
  const titleKey = postNum ? `news.post${postNum}.title` : '';
  const titleTranslation = titleKey ? t(titleKey) : '';
  const translatedTitle = titleKey && titleTranslation !== titleKey && titleTranslation !== '' ? titleTranslation : post.title;
  const excerptKey = postNum ? `news.post${postNum}.excerpt` : '';
  const excerptTranslation = excerptKey ? t(excerptKey) : '';
  const translatedExcerpt = excerptKey && excerptTranslation !== excerptKey ? excerptTranslation : post.excerpt;
  const dateKey = postNum ? `news.post${postNum}.date` : '';
  const dateTranslation = dateKey ? t(dateKey) : '';
  const translatedDate = dateKey && dateTranslation !== dateKey ? dateTranslation : post.date;
  const readTimeKey = postNum ? `news.post${postNum}.readTime` : '';
  const readTimeTranslation = readTimeKey ? t(readTimeKey) : '';
  const translatedReadTime = readTimeKey && readTimeTranslation !== readTimeKey ? readTimeTranslation : post.readTime;
  const fullStoryKey = postNum ? `news.post${postNum}.fullStory` : '';
  const fullStoryTranslation = fullStoryKey ? t(fullStoryKey) : '';
  const translatedFullStory = fullStoryKey && fullStoryTranslation !== fullStoryKey ? fullStoryTranslation : post.fullStory;
  const categoryKeyMap: Record<string, string> = {
    Political: 'news.catPolitical',
    Immigration: 'news.catImmigration',
    Business: 'news.catBusiness',
    'Note Worthy': 'news.catNoteWorthy',
    'Arts & Entertainment': 'news.catArtMedia',
    Events: 'news.catEvents',
    Sports: 'news.catSports'
  };
  const translatedCategory = categoryKeyMap[post.category] ? t(categoryKeyMap[post.category]) : post.category;
  const isSportsComposite = post.id === 'post-8' || post.players && post.players.length > 0;
  const isWabafu = post.id === 'post-12';
  const compositeImages = [{
    name: 'Oscar Avilez',
    url: 'https://cdn.magicpatterns.com/uploads/hkEmYVyvz28kSzeT5ukNrR/Oscar_AViles.png'
  }, {
    name: 'David Ruíz',
    url: 'https://cdn.magicpatterns.com/uploads/jX1nHu27oh4QsStqz4qtmu/David_ruiz.png'
  }, {
    name: 'Joseph Rosales',
    url: 'https://cdn.magicpatterns.com/uploads/qGfb5k5pbqZyGMPg5qDGSp/Joseph_Rosales.png'
  }, {
    name: 'Andy Nájar',
    url: 'https://cdn.magicpatterns.com/uploads/mQoPkEXd6jyhojvo6LYyV9/Any_Najar.png'
  }, {
    name: 'Bryan Acosta',
    url: 'https://cdn.magicpatterns.com/uploads/3zNFYQqGHoBrp1dCyKXGuJ/Bryan_Acosta.png'
  }];
  const hasMultipleImages = !!post.secondImage;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-lg group flex flex-col" data-id="element-2997">
      {isSportsComposite ? <div className="relative h-64 overflow-hidden flex-shrink-0 bg-gradient-to-b from-haa-midblue via-haa-blue to-haa-navy" data-id="element-2998">
          {/* Player Images */}
          <div className="absolute inset-0 flex items-end justify-center px-2 pb-12 sm:pb-10" data-id="element-2999">
            {compositeImages.map((img, i) => <motion.div key={img.name} initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2 + i * 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative w-1/5 -mx-1.5 sm:-mx-2" data-id="element-3000">
                <img src={img.url} alt={img.name} className="w-full h-auto object-contain drop-shadow-2xl" data-id="element-3001" />
              </motion.div>)}
          </div>
          {/* Gradient Overlay */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-haa-navy/80 to-transparent" data-id="element-3002" />
          {/* Top Left Badge */}
          <div className="absolute top-3 left-3" data-id="element-3003">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3004">
              {translatedCategory}
            </span>
          </div>
          {/* Bottom Text */}
          <div className="absolute bottom-0 left-0 right-0 p-5 pb-3" data-id="element-3005">
            <p className="text-white/90 font-nunito text-[10px] sm:text-xs font-bold tracking-widest mb-1 flex items-center gap-1.5" data-id="element-3006">
              <span className="text-lg leading-none" data-id="element-3007">🇭🇳</span>
              {isEs ? 'HONDUREÑOS EN LA MLS · TEMPORADA 2025-26' : 'HONDURANS IN MLS · 2025-26 SEASON'}
            </p>
          </div>
        </div> : isWabafu ? <div className="relative h-52 overflow-hidden flex-shrink-0" data-id="element-3008">
          <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center center'
      }} data-id="element-3009" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-3010" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-3011">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={inView ? {
          scale: 1,
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-3012">
              <span className="text-5xl mb-3 block" data-id="element-3013">🥁</span>
              <p className="font-bebas text-white text-2xl sm:text-3xl tracking-wider leading-tight" data-id="element-3014">
                {isEs ? 'CULTURA EN MOVIMIENTO' : 'CULTURE IN MOTION'}
              </p>
              <p className="font-nunito text-white/70 text-xs tracking-widest uppercase mt-1" data-id="element-3015">
                Wabafu Garifuna Dance Theater
              </p>
            </motion.div>
          </div>
          <div className="absolute top-3 left-3" data-id="element-3016">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3017">
              {translatedCategory}
            </span>
          </div>
        </div> : hasMultipleImages ? <div className="flex-shrink-0" data-id="element-3018">
          <div className="grid grid-cols-2 gap-0.5" data-id="element-3019">
            <div className="relative h-36 overflow-hidden" data-id="element-3020">
              <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: post.imagePosition ?? 'center 15%'
          }} data-id="element-3021" />
            </div>
            <div className="relative h-36 overflow-hidden" data-id="element-3022">
              <img src={post.secondImage!} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: 'center center'
          }} data-id="element-3023" />
            </div>
          </div>
          <div className="relative -mt-8 px-3 pb-0 z-10" data-id="element-3024">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3025">
              {translatedCategory}
            </span>
          </div>
        </div> : <div className="relative h-44 overflow-hidden flex-shrink-0" data-id="element-3026">
          {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center 15%'
      }} data-id="element-3027" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-3028">
              <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, white 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-3029" />
            </div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-3030" />
          <div className="absolute top-3 left-3" data-id="element-3031">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3032">
              {translatedCategory}
            </span>
          </div>
        </div>}

      <div className="p-5 flex flex-col flex-1" data-id="element-3033">
        <div className="flex items-center gap-2 mb-2" data-id="element-3034">
          <div className="w-4 h-0.5 bg-haa-blue rounded-full" data-id="element-3035" />
          <span className="font-nunito text-gray-400 text-xs" data-id="element-3036">
            {translatedDate}
          </span>
        </div>

        {!isSportsComposite && <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight mb-2" data-id="element-3037">
            {translatedTitle}
          </h3>}

        <div className="flex-1 mb-3" data-id="element-3038">
          {!isSportsComposite && <p className="font-nunito text-gray-500 text-sm leading-relaxed line-clamp-3" data-id="element-3039">
              {translatedExcerpt}
            </p>}
        </div>

        <div className="mb-4" data-id="element-3040">
          {post.storyUrl && post.storyUrl !== '#' ? <a href={post.storyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" data-id="element-3041">
              {t('news.readFullStory')}
              <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-3042" />
            </a> : post.fullStory || post.players || post.videoEmbeds ? <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" data-id="element-3043">
              {t('news.readFullStory')}
              <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-3044" />
            </button> : null}
        </div>

        {post.coverageLinks && post.coverageLinks.length > 0 && <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3" data-id="element-3045">
            <NewspaperIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-3046" />
            <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-3047">
              {t('news.alsoCoveredBy')}
            </span>
            {post.coverageLinks.map((link, i) => <Fragment key={i} data-id="element-3048">
                {i > 0 && <span className="text-gray-200 select-none" data-id="element-3049">·</span>}
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-3050">
                  {link.label}
                  <ExternalLinkIcon className="w-3 h-3" data-id="element-3051" />
                </a>
              </Fragment>)}
          </div>}
      </div>

      <div className="flex items-center justify-between px-5 pb-5 pt-3 border-t border-gray-100 mt-auto" data-id="element-3052">
        <div className="flex flex-wrap items-center gap-3 text-gray-400 text-xs" data-id="element-3053">
          <span className="flex items-center gap-1" data-id="element-3054">
            <CalendarIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-3055" />
            <span className="font-nunito" data-id="element-3056">{translatedDate}</span>
          </span>
        </div>
        <a href={`#/news/${post.id}?lang=${language}`} className="inline-flex items-center gap-1 text-haa-blue hover:text-haa-midblue font-nunito font-bold text-xs transition-colors" title={isEs ? 'Abrir historia' : 'Open story'} data-id="element-3057">
          <Share2Icon className="w-3.5 h-3.5" data-id="element-3058" />
          {isEs ? 'Compartir' : 'Share'}
        </a>
      </div>

      <StoryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} post={post} translatedTitle={translatedTitle} translatedDate={translatedDate} translatedFullStory={translatedFullStory} isEs={isEs} t={t} data-id="element-3059" />
    </motion.article>;
}
// Main Section
export function NewsBlog() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const [activeCategory, setActiveCategory] = useState('All');
  const {
    t
  } = useLanguage();
  const filtered = activeCategory === 'All' ? BLOG_POSTS : BLOG_POSTS.filter(p => p.category === activeCategory);
  const featured = filtered.find(p => p.featured) ?? null;
  const rest = filtered.filter(p => p.id !== featured?.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const categoryTranslations: Record<string, string> = {
    All: t('news.catAll'),
    Political: t('news.catPolitical'),
    Immigration: t('news.catImmigration'),
    Business: t('news.catBusiness'),
    'Note Worthy': t('news.catNoteWorthy'),
    Events: t('news.catEvents'),
    'Arts & Entertainment': t('news.catArtMedia'),
    Sports: t('news.catSports')
  };
  return <section id="news" className="bg-[#F8FAFF] py-16 lg:py-24" aria-labelledby="news-heading" data-id="element-3060">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3061">
        <div ref={titleRef} className="mb-10" data-id="element-3062">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-3063">
            <div data-id="element-3064">
              <span className="font-nunito text-haa-midblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-3065">
                {t('news.label')}
              </span>
              <h2 id="news-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-3066">
                {t('news.heading')}
              </h2>
              <div className="flex items-center gap-3 mt-4" data-id="element-3067">
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-3068" />
                <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-3069" />
                <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-3070" />
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15
      }} className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Filter by category" data-id="element-3071">
          {ALL_CATEGORIES.map(cat => <button key={cat} role="tab" aria-selected={activeCategory === cat} onClick={() => setActiveCategory(cat)} className={`relative font-nunito font-semibold text-sm px-5 py-2 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue ${activeCategory === cat ? 'bg-haa-navy text-white shadow-md' : 'bg-white text-gray-500 hover:text-haa-navy border border-gray-200 hover:border-haa-navy'}`} data-id="element-3072">
              <span className="inline-flex items-center gap-1.5" data-id="element-3073">
                {cat === 'Sports' && <TrophyIcon className="w-3.5 h-3.5" data-id="element-3074" />}
                {categoryTranslations[cat] ?? cat}
              </span>
            </button>)}
        </motion.div>

        <AnimatePresence mode="wait" data-id="element-3075">
          <motion.div key={activeCategory} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} data-id="element-3076">
            {filtered.length === 0 ? <div className="text-center py-20" data-id="element-3077">
                <TagIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-3078" />
                <p className="font-nunito text-gray-400 text-lg" data-id="element-3079">
                  {t('news.noPosts')}
                </p>
              </div> : activeCategory === 'All' ? <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3080">
                  {featured && <FeaturedCard post={featured} data-id="element-3081" />}
                  {rest.length > 0 && <div className="flex flex-col gap-6" data-id="element-3082">
                      {rest.slice(0, 2).map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-3083" />)}
                    </div>}
                </div>
                {rest.length > 2 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-id="element-3084">
                    {rest.slice(2).map((post, i) => <PostCard key={post.id} post={post} index={i + 2} data-id="element-3085" />)}
                  </div>}
              </> : <div className={`grid gap-6 ${filtered.length === 1 ? 'grid-cols-1 max-w-3xl mx-auto' : 'grid-cols-1 sm:grid-cols-2'}`} data-id="element-3086">
                {filtered.map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-3087" />)}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}